import { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEO from '@/components/feature/SEO';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ComicCard from '@/components/base/ComicCard';
import { comics, categories } from '@/mocks/comics';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState(slug || 'love');
  const [sortBy, setSortBy] = useState<'rating' | 'views' | 'update'>('rating');
  const [currentPage, setCurrentPage] = useState(1);
  const perPage = 8;

  useEffect(() => {
    if (slug) setActiveCategory(slug);
  }, [slug]);

  useEffect(() => {
    setCurrentPage(1);
  }, [activeCategory, sortBy]);

  const categoryName = useMemo(() => {
    const cat = categories.find((c) => c.slug === activeCategory);
    return cat ? cat.name : '全部';
  }, [activeCategory]);

  const filteredComics = useMemo(() => {
    let result = comics.filter((c) => c.category === activeCategory);
    if (sortBy === 'rating') result.sort((a, b) => b.rating - a.rating);
    if (sortBy === 'views') result.sort((a, b) => parseInt(b.views) - parseInt(a.views));
    if (sortBy === 'update') result.sort((a, b) => b.updateTime.localeCompare(a.updateTime));
    return result;
  }, [activeCategory, sortBy]);

  const totalPages = Math.ceil(filteredComics.length / perPage);
  const pagedComics = filteredComics.slice((currentPage - 1) * perPage, currentPage * perPage);

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${categoryName}漫画 - 韩漫星球`,
    description: `韩漫星球${categoryName}漫画分类，免费下拉式阅读，收录优质韩国${categoryName}漫画，每日更新。`,
    url: `https://hanmanxingqiu.com/category/${activeCategory}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '韩漫星球',
      url: 'https://hanmanxingqiu.com',
    },
  };

  return (
    <div className="min-h-screen bg-background-50">
      <ScrollToTop />
      <SEO
        title={`${categoryName}漫画_韩漫免费下拉式阅读_韩漫星球`}
        description={`韩漫星球${categoryName}漫画分类，收录优质韩国${categoryName}漫画大全，免费下拉式阅读，每日更新，资源多样操作简单。`}
        keywords={`${categoryName}漫画,韩漫免费,漫画下拉式阅读,韩国漫画${categoryName}`}
        canonicalUrl={`https://hanmanxingqiu.com/category/${activeCategory}`}
        jsonLd={jsonLd}
      />

      <div className="bg-white">
        <Navbar />
      </div>

      <main>
        <div className="w-full px-4 md:px-6 max-w-7xl mx-auto pt-24 pb-6">
          <Breadcrumb items={[{ label: '首页', to: '/' }, { label: `${categoryName}漫画` }]} />

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
            <h1 className="text-2xl md:text-3xl font-bold text-foreground-950" style={{ fontFamily: 'var(--font-heading)' }}>
              {categoryName}漫画
            </h1>
            <div className="flex items-center gap-2">
              <span className="text-xs text-foreground-400 whitespace-nowrap">排序：</span>
              {[
                { key: 'rating', label: '评分最高' },
                { key: 'views', label: '人气最热' },
                { key: 'update', label: '最近更新' },
              ].map((s) => (
                <button
                  key={s.key}
                  onClick={() => setSortBy(s.key as typeof sortBy)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${sortBy === s.key ? 'bg-rose-500 text-white' : 'bg-background-100 text-foreground-600 hover:bg-background-200'}`}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          {/* Category Tabs */}
          <div className="flex items-center gap-2 overflow-x-auto pb-4 mb-6 scrollbar-hide">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 ${activeCategory === cat.slug ? 'bg-rose-500 text-white shadow-sm' : 'bg-background-100 text-foreground-600 hover:bg-background-200'}`}
              >
                <i className={`${cat.icon} text-sm`} />
                {cat.name}
              </Link>
            ))}
          </div>

          {pagedComics.length > 0 ? (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 md:gap-5 mb-10">
                {pagedComics.map((comic) => (
                  <ComicCard key={comic.id} {...comic} />
                ))}
              </div>

              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 pb-10">
                  <button
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="w-9 h-9 flex items-center justify-center rounded-full bg-white border border-background-200 text-foreground-600 disabled:opacity-30 disabled:cursor-not-allowed hover:border-rose-300 transition-colors"
                  >
                    <i className="ri-arrow-left-s-line" />
                  </button>
                  {Array.from({ length: totalPages }).map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setCurrentPage(i + 1)}
                      className={`w-9 h-9 flex items-center justify-center rounded-full text-sm font-medium transition-colors ${currentPage === i + 1 ? 'bg-rose-500 text-white' : 'bg-white border border-background-200 text-foreground-600 hover:border-rose-300'}`}
                    >
                      {i + 1}
                    </button>
                  ))}
                  <button
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="w-9 h-9 flex items-center justify-center rounded-full bg-white border border-background-200 text-foreground-600 disabled:opacity-30 disabled:cursor-not-allowed hover:border-rose-300 transition-colors"
                  >
                    <i className="ri-arrow-right-s-line" />
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-20">
              <i className="ri-emotion-sad-line text-5xl text-foreground-300 mb-4 inline-block" />
              <p className="text-foreground-500">该分类暂无漫画，请浏览其他分类</p>
              <Link to="/" className="inline-flex items-center gap-1 text-rose-500 text-sm mt-3 hover:underline">
                <i className="ri-arrow-left-line" /> 返回首页
              </Link>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}